// frontend/src/common/modal/PractitionerQuickLookModal.jsx
/*
 * Practitioner quick look / edit modal.
 *
 * hideFinancialFields (Practitioner Lead / DEN72):
 *   - Edit + read modes: hide paid-based-on, associate fee, insurance, per-day, grid, include HYG, base pay UI.
 *   - Save: use buildPractitionerPersonalUpdatePayload (personal PATCH only), skip associate-fee validation.
 * Default false: existing behavior for organizer / admin / area manager.
 */

import { useEffect, useMemo, useState } from "react";
import { Badge, Button, Modal, FormField, Dropdown, CheckboxField, ConfirmModal } from "@common";
import { useClinicDetail } from "@api/hooks";
import { showError, showSuccess } from "@common/ui/Toast";

import { FORM_IDS } from "@utils";
import { safeText, normalizeTypeForUi, pickBadgeColor, clinicLabel } from "@utils/utils";
import {
  TYPE_OPTIONS,
  STATUS_OPTIONS,
  RATE_TYPE_OPTIONS,
  validatePctField,
  validateEmail,
  validateOtherProfit,
  validateOtherDeduction,
  normalizeOtherDeductionMode,
  OTHER_DEDUCTION_MODE_HOURLY,
  OTHER_DEDUCTION_MODE_AMOUNT,
  PRACTITIONER_NETSUITE_FIELDS,
  validateNetsuiteIds,
} from "@utils/practitionerFormUtils";
import {
  PAID_BASED_ON_OPTIONS,
  practitionerToForm,
  buildUpdatePayload, // full practitioner PATCH (managers)
  buildPractitionerPersonalUpdatePayload, // PL only — type, status, corp name, email, provider code
} from "@utils/practitionerQuickLookUtils";
import { useFormInteractionTime } from "@hooks/useFormInteractionTime";
import { useNetsuitePractitionerNames } from "@hooks/useNetsuitePractitionerNames";
import { useAuth } from "@api/useAuth";
import { isObserver, isOrganizerOrAdmin } from "@utils/roles";

function Row({ label, value }) {
  return (
    <div className="grid grid-cols-2 gap-4 py-2 border-b border-palette-gray-subtle">
      <div className="text-sm text-palette-gray-secondary">{label}</div>
      <div className="text-sm font-semibold text-palette-slate-primary text-right">
        {safeText(value) || "—"}
      </div>
    </div>
  );
}

function yesNo(v) {
  return v ? "Yes" : "No";
}

function pickBasePayRows(practitioner) {
  const arr = practitioner?.base_pay;
  if (Array.isArray(arr) && arr.length > 0) {
    return arr.filter((r) => r && (r.base_pay !== undefined || r.pay_percent !== undefined));
  }
  return [];
}

function getClinicSource(practitioner, fallbackClinicCode = "") {
  if (practitioner?.clinic) return practitioner.clinic;
  if (Array.isArray(practitioner?.clinics) && practitioner.clinics.length > 0) {
    return practitioner.clinics;
  }
  if (practitioner?.clinics) return practitioner.clinics;
  return fallbackClinicCode;
}

function getClinicAssignments(clinicValue) {
  if (Array.isArray(clinicValue)) {
    return clinicValue
      .map((clinic) => {
        if (!clinic) return null;
        if (typeof clinic === "string") {
          const code = safeText(clinic);
          return code ? { code, name: "", label: code } : null;
        }

        const code = safeText(clinic?.clinic || clinic?.code || clinic?.value);
        const name = safeText(clinic?.name || clinic?.clinic_name || clinic?.label);
        if (!code && !name) return null;
        return {
          code,
          name,
          label: name && code && name !== code ? `${name} (${code})` : name || code,
        };
      })
      .filter(Boolean);
  }

  if (clinicValue && typeof clinicValue === "object") {
    const code = safeText(clinicValue?.clinic || clinicValue?.code || clinicValue?.value);
    const name = safeText(clinicValue?.name || clinicValue?.clinic_name || clinicValue?.label);
    if (!code && !name) return [];
    return [
      {
        code,
        name,
        label: name && code && name !== code ? `${name} (${code})` : name || code,
      },
    ];
  }

  const code = safeText(clinicValue);
  return code ? [{ code, name: "", label: code }] : [];
}

export default function PractitionerQuickLookModal({
  isOpen,
  onClose,
  practitioner,
  onSave,
  canEdit = false,
  openInEdit = false,
  hideFinancialFields = false, // true for Practitioner Lead from ProfileDirectoryPage
  // Temporary hold: the editable type dropdown shows all practitioner types ONLY when
  // opened from Employee Profile (allowAllTypes). Everywhere else (financial modules) it is
  // restricted to Dentist/Hygienist so new types can't be selected there.
  allowAllTypes = false,
  fallbackClinicCode = "",
  highlightClinicBadgesTop = false,
  showUseThisButton = false,
  onUseThis,
  isUsingThis = false,
  useThisLabel = "Use this",
}) {
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [form, setForm] = useState(practitionerToForm(null));
  // Holds payload waiting for NetSuite-ID confirmation. When non-null,
  // confirm modal is shown so the admin can review changed NetSuite IDs
  // before they are persisted (these IDs feed downstream NetSuite postings
  // — Check / JournalEntry — so a bad value silently breaks accounting).
  const [pendingNetsuitePayload, setPendingNetsuitePayload] = useState(null);

  useEffect(() => {
    if (!practitioner) return;

    setForm(practitionerToForm(practitioner));

    if (openInEdit && canEdit) setIsEditing(true);
    else setIsEditing(false);
  }, [practitioner, openInEdit, canEdit]);

  const typeUi = useMemo(() => normalizeTypeForUi(practitioner?.practitioner_type), [practitioner]);

  // Editable type choices. Employee Profile passes allowAllTypes → all 16 types; financial
  // modules keep the default → Dentist/Hygienist only (temporary hold on new types).
  const typeOptions = useMemo(
    () =>
      allowAllTypes
        ? TYPE_OPTIONS
        : TYPE_OPTIONS.filter((o) => o.value === "" || o.value === "dds" || o.value === "hyg"),
    [allowAllTypes],
  );

  const feeMeta = useMemo(
    () => validatePctField(form.associate_fee_percentage),
    [form.associate_fee_percentage],
  );
  const feeInvalid = isEditing && feeMeta.hasAny && !feeMeta.ok;

  const emailMeta = useMemo(() => validateEmail(form.email), [form.email]);
  const emailInvalid = isEditing && emailMeta.hasAny && !emailMeta.ok;

  const otherProfitMeta = useMemo(
    () =>
      validateOtherProfit({
        enabled: !!form.other_profit_enabled,
        rows: form.other_profit_rows,
      }),
    [form.other_profit_enabled, form.other_profit_rows],
  );
  const otherProfitInvalid = isEditing && form.other_profit_enabled && !otherProfitMeta.ok;

  const otherDeductionMeta = useMemo(
    () =>
      validateOtherDeduction({
        enabled: !!form.other_deduction_enabled,
        rows: form.other_deduction_rows,
      }),
    [form.other_deduction_enabled, form.other_deduction_rows],
  );
  const otherDeductionInvalid = isEditing && form.other_deduction_enabled && !otherDeductionMeta.ok;

  const { user } = useAuth();
  const canEditNetsuiteIds = isOrganizerOrAdmin(user);
  // Observer sees the IDs read-only.
  const canViewNetsuiteIds = canEditNetsuiteIds || isObserver(user);

  const netsuiteMeta = useMemo(() => validateNetsuiteIds(form), [form]);
  // Resolve NetSuite vendor/account names so admins see "Name (#ID)" instead
  // of a bare ID. Source is the form (which mirrors the practitioner in view
  // mode and reflects in-progress edits in edit mode). Only fetched for
  // admin/organizer because the backend endpoints are admin-only.
  const netsuiteNames = useNetsuitePractitionerNames(canViewNetsuiteIds ? form : null);
  // Also resolve names for the *original* (saved) NetSuite IDs so the
  // confirmation summary can show "Old: Name (#id) → New: Name (#id)".
  const originalNetsuiteNames = useNetsuitePractitionerNames(
    canEditNetsuiteIds ? practitioner : null,
  );

  // Compute the diff for confirmation: each NetSuite field whose ID has
  // changed between the saved practitioner and the in-progress form.
  const netsuiteChanges = useMemo(() => {
    if (!canEditNetsuiteIds) return [];
    const norm = (v) => {
      if (v === null || v === undefined) return "";
      const s = String(v).trim();
      return s === "" ? "" : s;
    };
    return PRACTITIONER_NETSUITE_FIELDS.map(({ key, label }) => {
      const oldId = norm(practitioner?.[key]);
      const newId = norm(form?.[key]);
      if (oldId === newId) return null;
      return {
        key,
        label,
        oldId,
        newId,
        oldDisplay: oldId ? originalNetsuiteNames.getDisplayFor(key, oldId) : "",
        newDisplay: newId ? netsuiteNames.getDisplayFor(key, newId) : "",
      };
    }).filter(Boolean);
  }, [canEditNetsuiteIds, practitioner, form, netsuiteNames, originalNetsuiteNames]);

  const viewBasePayRows = useMemo(() => pickBasePayRows(practitioner), [practitioner]);
  const clinicSource = useMemo(
    () => getClinicSource(practitioner, fallbackClinicCode),
    [fallbackClinicCode, practitioner],
  );
  // Single parse: codes and display string are both derived from getClinicAssignments,
  // removing the need for separate formatClinicAssignments / getClinicCodes functions.
  const parsedClinics = useMemo(() => getClinicAssignments(clinicSource), [clinicSource]);
  const primaryClinicCode = parsedClinics.length === 1 ? parsedClinics[0].code : "";
  const { data: clinicDetail } = useClinicDetail(primaryClinicCode, {
    enabled: !!primaryClinicCode,
  });
  const { clinicDisplay, clinicBadgeItems } = useMemo(() => {
    const c = clinicDetail?.clinic;
    if (parsedClinics.length === 1 && c) {
      const label = clinicLabel(c);
      return {
        clinicDisplay: label,
        clinicBadgeItems: [
          { code: safeText(c.clinic), name: safeText(c.name || c.clinic_name), label },
        ],
      };
    }
    return {
      clinicDisplay: parsedClinics.map((i) => i.label).join(", "),
      clinicBadgeItems: parsedClinics,
    };
  }, [parsedClinics, clinicDetail]);

  const {
    formTrackingProps,
    recordSubmitSuccess,
    reset: resetInteractionTimer,
  } = useFormInteractionTime(FORM_IDS.PRACTITIONER_QUICK_LOOK);

  useEffect(() => {
    if (!isOpen) resetInteractionTimer();
  }, [isOpen, resetInteractionTimer]);

  if (!isOpen || !practitioner) return null;

  function updateField(key, val) {
    setForm((prev) => ({ ...prev, [key]: val }));
  }

  function resetToOriginal() {
    setForm(practitionerToForm(practitioner));
  }

  async function handleSave() {
    if (!canEdit) return showError("You do not have permission to edit.");

    // The name is the only human handle on a profile — every table, dropdown and
    // statement renders it, so an empty one is rejected here as well as server-side.
    if (!safeText(form.name).trim()) {
      showError("Name cannot be blank.");
      return;
    }
    if (!onSave) return showError("Update handler not wired yet.");

    // PL: associate fee input hidden — must not fail save on stale/empty fee in form state
    if (!hideFinancialFields) {
      if (feeMeta.hasAny && !feeMeta.ok) {
        showError("Associate fee percentage must be a valid number between 0 and 100.");
        return;
      }
    }

    if (emailMeta.hasAny && !emailMeta.ok) {
      showError("Please enter a valid email address.");
      return;
    }
    // PL flow does not touch other_profit — only validate when financial fields are shown
    if (!hideFinancialFields && form.other_profit_enabled && !otherProfitMeta.ok) {
      showError(
        "Each Other Profit tier needs a label and a percentage between 0 and 100 (exclusive).",
      );
      return;
    }

    // PL flow does not touch other_deduction — only validate when financial fields are shown
    if (!hideFinancialFields && form.other_deduction_enabled && !otherDeductionMeta.ok) {
      showError(
        "Each Other Deduction tier needs a label and a percentage between 0 and 100 (exclusive).",
      );
      return;
    }
    // NetSuite IDs (admin/organizer only) — must be positive integers when present
    if (canEditNetsuiteIds && !netsuiteMeta.ok) {
      showError("NetSuite IDs must be whole numbers greater than 0.");
      return;
    }
    // PL → personal-only PATCH; managers → full compensation + grid payload
    const payload = hideFinancialFields
      ? buildPractitionerPersonalUpdatePayload(form)
      : buildUpdatePayload(form, practitioner);

    // Strip NetSuite IDs for non-admin users so we don't accidentally null them out.
    if (!canEditNetsuiteIds) {
      for (const { key } of PRACTITIONER_NETSUITE_FIELDS) {
        delete payload[key];
      }
    }

    // If admin changed any NetSuite IDs, gate the save behind a confirm modal
    // that lists each old → new value (with names) so they can sanity-check
    // before we persist the change.
    if (canEditNetsuiteIds && netsuiteChanges.length > 0) {
      setPendingNetsuitePayload(payload);
      return;
    }

    await persistSave(payload);
  }

  async function persistSave(payload) {
    setIsSaving(true);
    try {
      await onSave(practitioner, payload);
      recordSubmitSuccess();
      showSuccess("Profile updated");
      setIsEditing(false);
      onClose?.();
    } catch (e) {
      showError(e?.message || "Failed to update profile");
    } finally {
      setIsSaving(false);
      setPendingNetsuitePayload(null);
    }
  }

  async function confirmNetsuiteChangesAndSave() {
    if (!pendingNetsuitePayload) return;
    await persistSave(pendingNetsuitePayload);
  }

  const footer = !isEditing ? (
    <>
      <Button variant="outline" onClick={onClose}>
        Close
      </Button>
      {showUseThisButton && (
        <Button onClick={() => onUseThis?.(practitioner)} disabled={isUsingThis}>
          {isUsingThis ? "Using..." : useThisLabel}
        </Button>
      )}
      {canEdit && <Button onClick={() => setIsEditing(true)}>Edit</Button>}
    </>
  ) : (
    <>
      <Button
        variant="outline"
        disabled={isSaving}
        onClick={() => {
          setIsEditing(false);
          resetToOriginal();
        }}
      >
        Cancel
      </Button>
      <Button
        onClick={handleSave}
        disabled={
          isSaving ||
          (!hideFinancialFields && feeMeta.hasAny && !feeMeta.ok) || // PL skips fee gate
          (emailMeta.hasAny && !emailMeta.ok) ||
          (!hideFinancialFields && form.other_profit_enabled && !otherProfitMeta.ok) ||
          (!hideFinancialFields && form.other_deduction_enabled && !otherDeductionMeta.ok) ||
          (canEditNetsuiteIds && !netsuiteMeta.ok)
        }
      >
        {isSaving ? "Saving..." : "Save"}
      </Button>
    </>
  );

  const paidBasedOnLabel =
    PAID_BASED_ON_OPTIONS.find((o) => o.value === form.paid_based_on)?.label ?? "";

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        title={safeText(practitioner?.name) || "—"}
        footer={footer}
        size="md"
      >
        <div className="space-y-4" {...formTrackingProps}>
          <div className="flex items-center justify-between gap-3">
            <div className="text-xs text-palette-gray-secondary">
              ID: {safeText(practitioner?.id) || "—"}
            </div>
            <Badge color={pickBadgeColor(typeUi)}>{typeUi || "—"}</Badge>
          </div>

          {isEditing ? (
            <div className="space-y-4">
              {/* --- PL may edit everything in this group, name included --- */}
              {/* The name lives on the shared profile, so a rename follows the
                  practitioner to every clinic they work at, not just this one. */}
              <FormField
                label="Name"
                name="name"
                value={form.name ?? ""}
                onChange={(e) => updateField("name", e.target.value)}
                placeholder="Practitioner name"
              />

              <Dropdown
                label="Type"
                name="practitioner_type"
                value={form.practitioner_type}
                onChange={(v) => updateField("practitioner_type", v)}
                options={typeOptions}
                placeholder={null}
              />

              <Dropdown
                label="Status"
                name="status"
                value={form.status}
                onChange={(v) => updateField("status", v)}
                options={STATUS_OPTIONS}
                placeholder={null}
              />

              <FormField
                label="Corporation Name"
                name="actual_name"
                value={form.actual_name ?? ""}
                onChange={(e) => updateField("actual_name", e.target.value)}
              />

              <FormField
                label="Email"
                name="email"
                type="email"
                value={form.email ?? ""}
                onChange={(e) => updateField("email", e.target.value)}
                error="Please enter a valid email address."
                showError={emailInvalid}
              />

              <FormField
                label="Provider Code"
                name="provider_code"
                value={form.provider_code ?? ""}
                onChange={(e) => updateField("provider_code", e.target.value)}
              />

              {/* --- Identity/employment: visible to all roles including Practice Lead --- */}
              <FormField
                label="Position ID"
                name="position_id"
                value={form.position_id ?? ""}
                onChange={(e) => updateField("position_id", e.target.value)}
              />

              <FormField
                label="Associate ID"
                name="associate_id"
                value={form.associate_id ?? ""}
                onChange={(e) => updateField("associate_id", e.target.value)}
                placeholder="e.g. GGNBMTGD9"
              />

              <Dropdown
                label="Rate Type"
                name="rate_type"
                value={form.rate_type ?? ""}
                onChange={(v) => updateField("rate_type", v)}
                options={RATE_TYPE_OPTIONS}
                placeholder="Select an option"
              />

              <FormField
                label="Hire Date"
                name="hire_date"
                type="date"
                value={form.hire_date ?? ""}
                onChange={(e) => updateField("hire_date", e.target.value)}
              />
              <FormField
                label="Supervisor"
                name="supervisor"
                value={form.supervisor ?? ""}
                onChange={(e) => updateField("supervisor", e.target.value)}
                placeholder="e.g. Katchikian, Sherry"
              />

              {/* --- PL: block below hidden (compensation / insurance / fee grid / payment-related) --- */}
              {!hideFinancialFields && (
                <>
                  <Dropdown
                    label="Paid Based On"
                    name="paid_based_on"
                    value={form.paid_based_on}
                    onChange={(v) => updateField("paid_based_on", v)}
                    options={PAID_BASED_ON_OPTIONS}
                    placeholder="Select an option"
                  />

                  <FormField
                    label="Associate Fee (%)"
                    name="associate_fee_percentage"
                    value={form.associate_fee_percentage ?? ""}
                    onChange={(e) => updateField("associate_fee_percentage", e.target.value)}
                    placeholder="e.g. 40%"
                    type="number"
                    min={0}
                    max={100}
                    step="0.01"
                    error={
                      feeInvalid
                        ? "Associate fee percentage must be a valid number between 0 and 100."
                        : undefined
                    }
                    showError={feeInvalid}
                  />

                  <FormField
                    label="Insurance"
                    name="insurance"
                    value={form.insurance ?? ""}
                    onChange={(e) => updateField("insurance", e.target.value)}
                    type="number"
                    step="0.01"
                  />

                  <div className="space-y-3">
                    <CheckboxField
                      label="Paid By Day"
                      checked={form.per_day}
                      onChange={(v) => updateField("per_day", v)}
                    />
                  </div>

                  {form.per_day && (
                    <FormField
                      label="Per Day Value"
                      name="per_day_value"
                      value={form.per_day_value ?? ""}
                      onChange={(e) => updateField("per_day_value", e.target.value)}
                      type="number"
                      step="0.01"
                    />
                  )}

                  <div className="space-y-3">
                    <CheckboxField
                      label="Grid"
                      checked={form.grid}
                      onChange={(v) => updateField("grid", v)}
                    />
                    <CheckboxField
                      label="Other Profit"
                      checked={!!form.other_profit_enabled}
                      onChange={(v) => updateField("other_profit_enabled", v)}
                    />
                    <CheckboxField
                      label="Other Deduction"
                      checked={!!form.other_deduction_enabled}
                      onChange={(v) => updateField("other_deduction_enabled", v)}
                    />
                  </div>

                  {form.other_profit_enabled && (
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <div className="text-sm font-medium text-palette-gray-heading">
                          Other Profit Tiers
                        </div>
                        <button
                          type="button"
                          className="text-xs text-palette-indigo-badge hover:underline disabled:opacity-50"
                          disabled={
                            form.__other_profit_rows_visible >= form.other_profit_rows.length
                          }
                          onClick={() => {
                            const next = Math.min(
                              form.other_profit_rows.length,
                              form.__other_profit_rows_visible + 1,
                            );
                            updateField("__other_profit_rows_visible", next);
                          }}
                        >
                          + Add tier
                        </button>
                      </div>
                      <p className="text-xs italic text-palette-gray-secondary">
                        The percentage here is always positive. On the monthly report, enter a
                        negative number if the amount should be deducted.
                      </p>
                      {form.other_profit_rows
                        .slice(0, form.__other_profit_rows_visible)
                        .map((row, idx) => (
                          <div key={idx} className="flex items-center gap-2">
                            <span className="w-6 text-xs text-palette-gray-placeholder">
                              {idx + 1}.
                            </span>
                            <input
                              type="text"
                              className="flex-1 rounded-lg border border-palette-gray-divider px-3 py-2 text-sm"
                              value={row.label}
                              onChange={(e) => {
                                const next = [...form.other_profit_rows];
                                next[idx] = { ...next[idx], label: e.target.value };
                                updateField("other_profit_rows", next);
                              }}
                              placeholder="Label (e.g. Referral Bonus)"
                            />
                            <input
                              type="number"
                              min={0}
                              max={100}
                              step="0.01"
                              className="w-28 rounded-lg border border-palette-gray-divider px-3 py-2 text-sm"
                              value={row.percent}
                              onChange={(e) => {
                                const next = [...form.other_profit_rows];
                                next[idx] = { ...next[idx], percent: e.target.value };
                                updateField("other_profit_rows", next);
                              }}
                              placeholder="%"
                            />
                            {idx >= 1 && idx === form.__other_profit_rows_visible - 1 && (
                              <Button
                                type="button"
                                variant="link"
                                size="sm"
                                className="text-xs text-palette-red-label hover:text-palette-red-badge hover:underline"
                                onClick={() => {
                                  const next = [...form.other_profit_rows];
                                  next[idx] = { label: "", percent: "" };
                                  updateField("other_profit_rows", next);
                                  updateField(
                                    "__other_profit_rows_visible",
                                    form.__other_profit_rows_visible - 1,
                                  );
                                }}
                              >
                                ✕
                              </Button>
                            )}
                          </div>
                        ))}
                      {otherProfitInvalid && (
                        <div className="text-xs text-palette-red-badge">
                          Each tier needs a label and a percentage greater than 0 and up to 100.
                        </div>
                      )}
                    </div>
                  )}

                  {form.other_deduction_enabled && (
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <div className="text-sm font-medium text-palette-gray-heading">
                          Other Deduction Tiers
                        </div>
                        <button
                          type="button"
                          className="text-xs text-palette-indigo-badge hover:underline disabled:opacity-50"
                          disabled={
                            form.__other_deduction_rows_visible >= form.other_deduction_rows.length
                          }
                          onClick={() => {
                            const next = Math.min(
                              form.other_deduction_rows.length,
                              form.__other_deduction_rows_visible + 1,
                            );
                            updateField("__other_deduction_rows_visible", next);
                          }}
                        >
                          + Add tier
                        </button>
                      </div>
                      {form.other_deduction_rows
                        .slice(0, form.__other_deduction_rows_visible)
                        .map((row, idx) => (
                          <div key={idx} className="flex items-center gap-2">
                            <span className="w-6 text-xs text-palette-gray-placeholder">
                              {idx + 1}.
                            </span>
                            <input
                              type="text"
                              className="flex-1 rounded-lg border border-palette-gray-divider px-3 py-2 text-sm"
                              value={row.label}
                              onChange={(e) => {
                                const next = [...form.other_deduction_rows];
                                next[idx] = { ...next[idx], label: e.target.value };
                                updateField("other_deduction_rows", next);
                              }}
                              placeholder="Label (e.g. Lab Share)"
                            />
                            {/* How the month's figure is entered. "Hourly"
                                keeps the $/hr rate here and asks for hours each
                                month; "Amount" asks for the dollar figure
                                directly, with no rate to hold. */}
                            <select
                              className="w-28 rounded-lg border border-palette-gray-divider px-2 py-2 text-sm"
                              value={normalizeOtherDeductionMode(row.mode)}
                              onChange={(e) => {
                                const next = [...form.other_deduction_rows];
                                const mode = e.target.value;
                                next[idx] = {
                                  ...next[idx],
                                  mode,
                                  // Switching to a fixed amount drops the rate,
                                  // so a stale figure can't be saved alongside it.
                                  hour_rate:
                                    mode === OTHER_DEDUCTION_MODE_AMOUNT ? "" : next[idx].hour_rate,
                                };
                                updateField("other_deduction_rows", next);
                              }}
                            >
                              <option value={OTHER_DEDUCTION_MODE_HOURLY}>Hourly</option>
                              <option value={OTHER_DEDUCTION_MODE_AMOUNT}>Amount</option>
                            </select>
                            {normalizeOtherDeductionMode(row.mode) ===
                            OTHER_DEDUCTION_MODE_AMOUNT ? (
                              <span className="w-28 px-1 text-xs italic text-palette-gray-placeholder">
                                Entered monthly
                              </span>
                            ) : (
                              <input
                                type="number"
                                min={0}
                                step="0.01"
                                className="w-28 rounded-lg border border-palette-gray-divider px-3 py-2 text-sm"
                                value={row.hour_rate}
                                onChange={(e) => {
                                  const next = [...form.other_deduction_rows];
                                  next[idx] = { ...next[idx], hour_rate: e.target.value };
                                  updateField("other_deduction_rows", next);
                                }}
                                placeholder="$/hr"
                              />
                            )}
                            {idx >= 1 && idx === form.__other_deduction_rows_visible - 1 && (
                              <Button
                                type="button"
                                variant="link"
                                size="sm"
                                className="text-xs text-palette-red-label hover:text-palette-red-badge hover:underline"
                                onClick={() => {
                                  const next = [...form.other_deduction_rows];
                                  next[idx] = {
                                    label: "",
                                    mode: OTHER_DEDUCTION_MODE_HOURLY,
                                    hour_rate: "",
                                  };
                                  updateField("other_deduction_rows", next);
                                  updateField(
                                    "__other_deduction_rows_visible",
                                    form.__other_deduction_rows_visible - 1,
                                  );
                                }}
                              >
                                ✕
                              </Button>
                            )}
                          </div>
                        ))}
                      {otherDeductionInvalid && (
                        <div className="text-xs text-palette-red-badge">
                          Each tier needs a label, and an hourly tier also needs a non-negative hour
                          rate.
                        </div>
                      )}
                    </div>
                  )}

                  {form.grid && (
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <div className="text-sm font-medium text-palette-gray-heading">
                          Base Pay Tiers
                        </div>
                        <button
                          type="button"
                          className="text-xs text-palette-indigo-badge hover:underline"
                          onClick={() => {
                            const next = [...form.base_pay_rows, { base_pay: "", pay_percent: "" }];
                            updateField("base_pay_rows", next);
                            updateField("__grid_rows_visible", form.__grid_rows_visible + 1);
                          }}
                        >
                          + Add tier
                        </button>
                      </div>
                      {form.base_pay_rows.slice(0, form.__grid_rows_visible).map((row, idx) => (
                        <div key={idx} className="flex items-center gap-2">
                          <span className="w-6 text-xs text-palette-gray-placeholder">
                            {idx + 1}.
                          </span>
                          <input
                            type="number"
                            className="w-32 rounded-lg border border-palette-gray-divider px-3 py-2 text-sm"
                            value={row.base_pay}
                            onChange={(e) => {
                              const next = [...form.base_pay_rows];
                              next[idx] = { ...next[idx], base_pay: e.target.value };
                              updateField("base_pay_rows", next);
                            }}
                            placeholder="Base Pay"
                          />
                          <input
                            type="number"
                            className="w-32 rounded-lg border border-palette-gray-divider px-3 py-2 text-sm"
                            value={row.pay_percent}
                            onChange={(e) => {
                              const next = [...form.base_pay_rows];
                              next[idx] = { ...next[idx], pay_percent: e.target.value };
                              updateField("base_pay_rows", next);
                            }}
                            placeholder="Pay %"
                          />
                          {idx >= 1 && idx === form.__grid_rows_visible - 1 && (
                            <Button
                              type="button"
                              variant="link"
                              size="sm"
                              className="text-xs text-palette-red-label hover:text-palette-red-badge hover:underline"
                              onClick={() => {
                                const next = [...form.base_pay_rows];
                                next[idx] = { base_pay: "", pay_percent: "" };
                                updateField("base_pay_rows", next);
                                updateField("__grid_rows_visible", form.__grid_rows_visible - 1);
                              }}
                            >
                              ✕
                            </Button>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}

              {canEditNetsuiteIds && (
                <div className="space-y-3 rounded-xl border border-palette-gray-divider p-3">
                  <div className="text-sm font-semibold text-palette-slate-primary">
                    NetSuite IDs
                  </div>
                  <div className="text-xs text-palette-gray-secondary">
                    Admin only. Each ID must be a whole number greater than 0.
                  </div>
                  {PRACTITIONER_NETSUITE_FIELDS.map((f) => {
                    const meta = netsuiteMeta.fields[f.key];
                    const invalid = meta?.hasAny && !meta?.ok;
                    const resolvedName = netsuiteNames.getName(f.key, form[f.key]);
                    const hasId = !!safeText(form[f.key]).trim();
                    const pending = netsuiteNames.isPendingFor(f.key, form[f.key]);
                    return (
                      <div key={f.key}>
                        <FormField
                          label={f.label}
                          name={f.key}
                          value={form[f.key] ?? ""}
                          onChange={(e) => {
                            // Digits only, no commas/decimals
                            const raw = String(e.target.value).replace(/[^\d]/g, "");
                            updateField(f.key, raw);
                          }}
                          type="text"
                          inputMode="numeric"
                          pattern="[0-9]*"
                          placeholder="Optional"
                          error={invalid ? "Must be a whole number greater than 0." : undefined}
                          showError={!!invalid}
                        />
                        {hasId && !invalid && (
                          <div className="mt-1 text-xs">
                            {pending ? (
                              <span className="text-palette-gray-secondary">Looking up name…</span>
                            ) : resolvedName ? (
                              <span className="font-semibold text-palette-indigo-heading">
                                {resolvedName}
                              </span>
                            ) : (
                              <span className="italic text-palette-amber-badge">
                                No matching NetSuite record found.
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          ) : (
            <div>
              {highlightClinicBadgesTop && (
                <div className="mb-4 rounded-xl border border-palette-slate-divider bg-palette-slate-soft px-4 py-3">
                  <div className="text-xs font-semibold uppercase tracking-wide text-palette-slate-secondary">
                    Clinic Match
                  </div>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {clinicBadgeItems.length > 0 ? (
                      clinicBadgeItems.map((clinic) => (
                        <Badge
                          key={clinic.code || clinic.label}
                          color="neutral"
                          title={clinic.name || clinic.code || clinic.label}
                        >
                          {clinic.label}
                        </Badge>
                      ))
                    ) : (
                      <Badge color="gray">—</Badge>
                    )}
                  </div>
                </div>
              )}

              {/* --- Read mode: basic rows always (PL + everyone) --- */}
              <Row label="Name" value={practitioner?.name} />
              <Row
                label="Type"
                value={
                  TYPE_OPTIONS.find((opt) => opt.value === form.practitioner_type)?.label ||
                  practitioner?.practitioner_type
                }
              />
              <Row
                label="Status"
                value={
                  STATUS_OPTIONS.find((opt) => opt.value === form.status)?.label ||
                  practitioner?.status
                }
              />
              <Row label="Corporation Name" value={practitioner?.actual_name} />
              <Row label="Email" value={practitioner?.email} />
              <Row label="Provider Code" value={practitioner?.provider_code} />
              {/* --- Identity/employment: visible to all roles including Practice Lead --- */}
              <Row label="Position ID" value={practitioner?.position_id} />
              <Row label="Associate ID" value={practitioner?.associate_id} />
              <Row label="Rate Type" value={practitioner?.rate_type} />
              <Row label="Hire Date" value={practitioner?.hire_date} />
              <Row label="Supervisor" value={practitioner?.supervisor} />
              {/* --- Read mode: financial rows only when not PL (same split as edit mode) --- */}
              {!hideFinancialFields && (
                <>
                  <Row
                    label="Paid Based On"
                    value={paidBasedOnLabel || practitioner?.paid_based_on}
                  />
                  <Row label="Associate Fee (%)" value={practitioner?.associate_fee_percentage} />
                  <Row label="Per day" value={yesNo(!!practitioner?.per_day)} />
                  {!!practitioner?.per_day && (
                    <Row label="Per Day Value" value={practitioner?.per_day_value} />
                  )}
                  {!highlightClinicBadgesTop && <Row label="Clinic" value={clinicDisplay} />}
                  <Row label="Insurance" value={practitioner?.insurance} />
                  <Row label="Grid" value={yesNo(!!practitioner?.grid)} />
                  <Row label="Include HYG" value={yesNo(!!practitioner?.include_hyg)} />
                  <Row label="Other Profit" value={yesNo(!!form.other_profit_enabled)} />
                  {form.other_profit_enabled &&
                    form.other_profit_rows
                      .slice(0, form.__other_profit_rows_visible)
                      .map((row, idx) => {
                        const hasAny =
                          safeText(row.label).trim() !== "" || safeText(row.percent).trim() !== "";
                        if (!hasAny) return null;
                        const suffix = form.__other_profit_rows_visible > 1 ? ` ${idx + 1}` : "";
                        return (
                          <div key={idx}>
                            <Row label={`Other Profit${suffix} Label`} value={row.label} />
                            <Row label={`Other Profit${suffix} (%)`} value={row.percent} />
                          </div>
                        );
                      })}

                  <Row label="Other Deduction" value={yesNo(!!form.other_deduction_enabled)} />
                  {form.other_deduction_enabled &&
                    form.other_deduction_rows
                      .slice(0, form.__other_deduction_rows_visible)
                      .map((row, idx) => {
                        const hasAny =
                          safeText(row.label).trim() !== "" ||
                          safeText(row.hour_rate).trim() !== "";
                        if (!hasAny) return null;
                        const suffix = form.__other_deduction_rows_visible > 1 ? ` ${idx + 1}` : "";
                        const isAmount =
                          normalizeOtherDeductionMode(row.mode) === OTHER_DEDUCTION_MODE_AMOUNT;
                        return (
                          <div key={`od-${idx}`}>
                            <Row label={`Other Deduction${suffix} Label`} value={row.label} />
                            <Row
                              label={`Other Deduction${suffix}`}
                              value={isAmount ? "Amount entered monthly" : "Hourly"}
                            />
                            {!isAmount && (
                              <Row
                                label={`Other Deduction${suffix} ($/hr)`}
                                value={row.hour_rate}
                              />
                            )}
                          </div>
                        );
                      })}

                  {viewBasePayRows.length > 0 ? (
                    viewBasePayRows.map((row, idx) => (
                      <div key={idx}>
                        <Row
                          label={`Base Pay${viewBasePayRows.length > 1 ? ` ${idx + 1}` : ""}`}
                          value={safeText(row.base_pay)}
                        />
                        <Row
                          label={`Base Pay${viewBasePayRows.length > 1 ? ` ${idx + 1}` : ""} %`}
                          value={safeText(row.pay_percent)}
                        />
                      </div>
                    ))
                  ) : (
                    <>
                      <Row label="Base Pay" value="" />
                      <Row label="Base Pay (%)" value="" />
                    </>
                  )}
                </>
              )}
              {canViewNetsuiteIds &&
                PRACTITIONER_NETSUITE_FIELDS.map((f) => {
                  const id = practitioner?.[f.key];
                  const idStr = id != null && id !== "" ? String(id) : "";
                  const name = netsuiteNames.getName(f.key, id);
                  if (!idStr) {
                    return <Row key={f.key} label={f.label} value="" />;
                  }
                  return (
                    <div
                      key={f.key}
                      className="grid grid-cols-2 gap-4 py-2 border-b border-palette-gray-subtle"
                    >
                      <div className="text-sm text-palette-gray-secondary">{f.label}</div>
                      <div className="text-right">
                        {name ? (
                          <span className="text-sm font-semibold text-palette-indigo-heading">
                            {name}
                          </span>
                        ) : (
                          <span className="text-sm italic text-palette-gray-placeholder">
                            No NetSuite match
                          </span>
                        )}
                        <span className="ml-2 text-xs font-mono text-palette-gray-secondary">
                          #{idStr}
                        </span>
                      </div>
                    </div>
                  );
                })}
            </div>
          )}
        </div>
      </Modal>
      <ConfirmModal
        isOpen={!!pendingNetsuitePayload}
        onClose={() => {
          if (!isSaving) setPendingNetsuitePayload(null);
        }}
        onConfirm={confirmNetsuiteChangesAndSave}
        title="Confirm NetSuite ID changes"
        size="md"
        confirmText={isSaving ? "Saving..." : "Confirm & Save"}
        cancelText="Go Back"
        confirmVariant="primary"
        isLoading={isSaving}
        message={
          <div className="space-y-3">
            <p className="text-sm text-palette-gray-heading">
              You are about to change one or more NetSuite IDs on{" "}
              <span className="font-semibold text-palette-slate-primary">
                {safeText(practitioner?.name) || "this practitioner"}
              </span>
              . These IDs drive Check &amp; Journal Entry postings, so please verify the new records
              are correct before saving.
            </p>
            <div className="rounded border border-palette-amber-border bg-palette-amber-soft p-3">
              <ul className="space-y-2">
                {netsuiteChanges.map((change) => (
                  <li key={change.key} className="text-sm">
                    <div className="font-semibold text-palette-slate-primary">{change.label}</div>
                    <div className="grid grid-cols-[auto,1fr] gap-x-2 gap-y-0.5 mt-0.5">
                      <span className="text-xs uppercase tracking-wide text-palette-gray-secondary">
                        Old
                      </span>
                      <span className="text-palette-gray-heading">
                        {change.oldId ? (
                          change.oldDisplay || (
                            <span className="font-mono text-palette-gray-secondary">
                              #{change.oldId}
                            </span>
                          )
                        ) : (
                          <span className="italic text-palette-gray-placeholder">(empty)</span>
                        )}
                      </span>
                      <span className="text-xs uppercase tracking-wide text-palette-indigo-badge">
                        New
                      </span>
                      <span className="font-semibold text-palette-indigo-heading">
                        {change.newId ? (
                          change.newDisplay || <span className="font-mono">#{change.newId}</span>
                        ) : (
                          <span className="italic text-palette-amber-badge">(cleared)</span>
                        )}
                      </span>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
            <p className="text-xs text-palette-gray-secondary">
              Tip: a missing name next to an ID means NetSuite has no matching record — double check
              before confirming.
            </p>
          </div>
        }
      />
    </>
  );
}
